<script setup>
import {computed, nextTick, onMounted, ref, useId, watch} from 'vue'
import {useNotificationStore} from '@/stores/NotificationStore.js'
import tagOptionLabel from './_optionLabel.vue'

// Drop-in replacement for <tags-multi-assign>: same props, events and slots.
const props = defineProps({
    selectedLeads: {
        type: Array,
        required: true,
    },
    showOutreachInitiate: {
        type: Boolean,
        default: false,
    },
    showTopbar: {
        type: Boolean,
        required: false,
        default: true,
    },
    // Tags already on the lead(s); these are left out of the list
    selectedTagIds: {
        type: Array,
        required: false,
        default: () => [],
    },
    instantAssign: {
        type: Boolean,
        required: false,
        default: false,
    },
})

const emit = defineEmits(['close', 'doReloadLeads'])

const notificationStore = useNotificationStore()
const listboxId = useId()

const searchInput = ref(null)

const availableTags = ref([])
const loadingTags = ref(true)
const working = ref(false)
const creating = ref(false)
const search = ref('')
const selectedTagId = ref(null)
const highlightedIndex = ref(-1)

const loadTags = async () => {
    loadingTags.value = true
    try {
        const response = await axios.get(route('crm.leadtags.assignable.index'))
        availableTags.value = response.data.tags
    } catch (error) {
        console.error('Error loading tags:', error)
    } finally {
        loadingTags.value = false
    }

    await nextTick()
    searchInput.value?.focus()
}

const searchTerm = computed(() => search.value.trim().toLowerCase())

const assignableTags = computed(() => (
    availableTags.value.filter((tag) => ! props.selectedTagIds.includes(tag.id))
))

const filteredTags = computed(() => {
    if (! searchTerm.value) {
        return assignableTags.value
    }

    return assignableTags.value.filter((tag) => tag.name.toLowerCase().includes(searchTerm.value))
})

const canCreate = computed(() => (
    searchTerm.value.length > 0
    && ! availableTags.value.some((tag) => tag.name.toLowerCase() === searchTerm.value)
))

// The "create" row sits after the tags, so it shares the same keyboard index space
const createOptionIndex = computed(() => filteredTags.value.length)
const optionCount = computed(() => filteredTags.value.length + (canCreate.value ? 1 : 0))

const optionId = (index) => `${listboxId}-option-${index}`

const canAssign = computed(() => selectedTagId.value !== null && props.selectedLeads.length > 0)

const assignTag = async (tagId) => {
    if (tagId === null || props.selectedLeads.length === 0 || working.value) {
        return
    }

    working.value = true
    try {
        const response = await axios.post(route('crm.assignTags', tagId), {
            selected: props.selectedLeads,
        })

        search.value = ''
        selectedTagId.value = null

        if (! props.instantAssign) {
            notificationStore.addNotification({
                type: 'success',
                title: response.data.tag.name,
                message: _mft('crm:tag.assigned'),
                dismissAfter: 2500,
            })
        }

        emit('doReloadLeads')
        emit('close')
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            message: _mft('error:error.generalOccurred'),
            dismissAfter: 2500,
        })
    } finally {
        working.value = false
        searchInput.value?.focus()
    }
}

const chooseTag = (tag) => {
    if (props.instantAssign) {
        assignTag(tag.id)
        return
    }

    selectedTagId.value = selectedTagId.value === tag.id ? null : tag.id
}

const createTag = async () => {
    const name = search.value.trim()
    if (! canCreate.value || creating.value) {
        return
    }

    creating.value = true
    try {
        const response = await axios.post(route('crm.leadtags.store'), {name})

        // The store endpoint answers 200 with `error` when the name is already taken
        const tag = response.data.tag
            ?? availableTags.value.find((existing) => existing.name.toLowerCase() === name.toLowerCase())

        if (! tag) {
            notificationStore.addNotification({
                type: 'error',
                message: response.data.error ?? _mft('error:error.generalOccurred'),
                dismissAfter: 2500,
            })
            return
        }

        if (! availableTags.value.some((existing) => existing.id === tag.id)) {
            availableTags.value = [...availableTags.value, tag]
                .sort((a, b) => a.name.localeCompare(b.name))
        }

        search.value = ''
        selectedTagId.value = null
        chooseTag(tag)
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            message: _mft('error:error.generalOccurred'),
            dismissAfter: 2500,
        })
    } finally {
        creating.value = false
    }
}

const activateHighlighted = () => {
    const index = highlightedIndex.value
    if (index < 0 || index >= optionCount.value) {
        // Nothing highlighted: Enter confirms the current selection
        if (! props.instantAssign && canAssign.value) {
            assignTag(selectedTagId.value)
        }
        return
    }

    if (index === createOptionIndex.value && canCreate.value) {
        createTag()
    } else {
        chooseTag(filteredTags.value[index])
    }
}

const moveHighlight = (step) => {
    if (optionCount.value === 0) {
        return
    }

    const start = highlightedIndex.value === -1 && step < 0 ? 0 : highlightedIndex.value
    highlightedIndex.value = (start + step + optionCount.value) % optionCount.value
}

const onSearchKeydown = (e) => {
    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault()
            moveHighlight(1)
            break
        case 'ArrowUp':
            e.preventDefault()
            moveHighlight(-1)
            break
        case 'Enter':
            e.preventDefault()
            activateHighlighted()
            break
        case 'Escape':
            // Only swallow Escape when it clears the search, so a surrounding
            // slideout or modal still gets to close on the next press
            if (search.value !== '') {
                e.stopPropagation()
                search.value = ''
            }
            break
    }
}

const close = () => {
    emit('close')
}

const closeAndUpdateLeads = () => {
    emit('doReloadLeads')
    emit('close')
}

watch(search, () => {
    highlightedIndex.value = searchTerm.value && optionCount.value > 0 ? 0 : -1
})

onMounted(() => {
    loadTags()
})
</script>

<template>
    <div>
        <div
            v-if="showTopbar"
            class="flex items-center justify-between gap-2 mb-3"
        >
            <span class="text-sm text-gray-500">
                {{ _mft('crm:tag.leadsSelected', {count: selectedLeads.length}) }}
            </span>
            <button-base
                size="sm"
                style-type="secondary"
                @click="close"
            >
                {{ _mft('shared:cancel') }}
            </button-base>
        </div>

        <div class="relative">
            <i class="absolute text-sm text-gray-400 -translate-y-1/2 pointer-events-none fa-regular fa-magnifying-glass left-3 top-1/2"></i>
            <input
                ref="searchInput"
                v-model="search"
                type="text"
                role="combobox"
                autocomplete="off"
                data-1p-ignore="true"
                maxlength="24"
                class="block w-full py-2 text-gray-900 bg-white border border-gray-300 rounded-md pl-9 pr-9 min-h-[2.625rem] transition-colors hover:border-gray-400 focus:border-myflowPurple focus:ring-1 focus:ring-myflowPurple/20 focus:outline-none placeholder:text-gray-400 disabled:bg-gray-50"
                :placeholder="_mft('crm:tag.searchTags')"
                :disabled="loadingTags"
                :aria-expanded="true"
                :aria-controls="listboxId"
                :aria-activedescendant="highlightedIndex >= 0 ? optionId(highlightedIndex) : null"
                @keydown="onSearchKeydown"
            />
            <button
                v-if="search"
                type="button"
                class="absolute flex items-center justify-center text-gray-400 -translate-y-1/2 rounded-md right-1.5 top-1/2 w-7 h-7 hover:text-gray-600 hover:bg-gray-100"
                :aria-label="_mft('shared:action.clear')"
                @click="search = ''; searchInput?.focus()"
            >
                <i class="fa-solid fa-circle-xmark"></i>
            </button>
        </div>

        <div
            class="relative mt-2 overflow-hidden bg-white border border-gray-200 rounded-xl"
            :class="working ? 'pointer-events-none' : ''"
        >
            <ul
                :id="listboxId"
                role="listbox"
                class="py-1 overflow-y-auto transition-opacity max-h-72"
                :class="working ? 'opacity-40' : ''"
            >
                <template v-if="loadingTags">
                    <li
                        v-for="n in 4"
                        :key="n"
                        class="flex items-center gap-3 px-3 py-2.5"
                    >
                        <span class="w-2.5 h-2.5 bg-gray-200 rounded-full animate-pulse shrink-0"></span>
                        <span
                            class="h-3 bg-gray-200 rounded animate-pulse"
                            :class="['w-32', 'w-24', 'w-40', 'w-28'][n - 1]"
                        ></span>
                    </li>
                </template>

                <template v-else>
                    <li
                        v-for="(tag, index) in filteredTags"
                        :id="optionId(index)"
                        :key="tag.id"
                        role="option"
                        :aria-selected="selectedTagId === tag.id"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer"
                        :class="[
                            index === highlightedIndex ? 'bg-gray-100' : '',
                            selectedTagId === tag.id ? 'bg-myflowLavender-light' : '',
                        ]"
                        @mousedown.prevent
                        @mouseenter="highlightedIndex = index"
                        @click="chooseTag(tag)"
                    >
                        <tag-option-label
                            :tag="tag"
                            :emphasized="selectedTagId === tag.id"
                        />

                        <i
                            v-if="instantAssign"
                            class="text-xs text-gray-400 fa-regular fa-plus shrink-0"
                            :class="index === highlightedIndex ? '' : 'invisible'"
                        ></i>
                        <i
                            v-else-if="selectedTagId === tag.id"
                            class="text-sm fa-solid fa-check text-myflowPurple shrink-0"
                        ></i>
                    </li>

                    <li
                        v-if="filteredTags.length === 0 && ! canCreate"
                        class="px-3 py-2 text-sm text-gray-500"
                    >
                        {{ _mft('crm:tag.noTagsFound') }}
                    </li>

                    <li
                        v-if="canCreate"
                        :id="optionId(createOptionIndex)"
                        role="option"
                        :aria-selected="false"
                        class="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 cursor-pointer"
                        :class="[
                            highlightedIndex === createOptionIndex ? 'bg-gray-100' : '',
                            filteredTags.length > 0 ? 'border-t border-gray-100' : '',
                            creating ? 'opacity-50 pointer-events-none' : '',
                        ]"
                        @mousedown.prevent
                        @mouseenter="highlightedIndex = createOptionIndex"
                        @click="createTag"
                    >
                        <i class="w-2.5 text-center fa-regular fa-plus shrink-0"></i>
                        <span class="truncate">
                            {{ _mft('crm:tag.createNew') }}: <span class="font-medium text-gray-900">{{ search.trim() }}</span>
                        </span>
                    </li>
                </template>
            </ul>

            <div
                v-if="working"
                class="absolute inset-0 flex items-center justify-center"
            >
                <mf-spinner-medium class="w-12 h-6"></mf-spinner-medium>
            </div>
        </div>

        <div
            v-if="$slots.actions || ! instantAssign"
            class="flex items-center justify-end gap-2 mt-3"
        >
            <slot name="actions"></slot>
            <button-base
                v-if="! instantAssign"
                style-type="primary"
                :disabled="! canAssign"
                :state="working ? 'waiting' : ''"
                @click="assignTag(selectedTagId)"
            >
                <span v-if="canAssign">
                    {{ _mft('crm:tag.assignToNLeads', {count: selectedLeads.length}) }}
                </span>
                <span v-else>{{ _mft('crm:tag.selectTagToAssign') }}</span>
            </button-base>
        </div>

        <outreach-multi-initiate
            v-if="showOutreachInitiate"
            class="pt-3 mt-3 border-t border-gray-100"
            :selected-leads="selectedLeads"
            @outreachInitiated="closeAndUpdateLeads"
        />
    </div>
</template>

<style>

</style>
